/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import "./Issues.scss";

import React, { FunctionComponent, useCallback, useEffect, useState} from "react";
import { PropertyDescription, PropertyRecord,  PropertyValue, PropertyValueFormat} from "@itwin/appui-abstract";
import { CellItem, ColumnDescription, FilterRenderer, RowItem, SimpleTableDataProvider, Table } from "@itwin/components-react";
import { useActiveIModelConnection } from "@itwin/appui-react";
import { Dialog } from "@itwin/core-react";
import { Button } from "@itwin/itwinui-react";
import { ModalDialogManager, ModelessDialogManager } from "@itwin/appui-react";

import { cIssue, IssueData } from "./Issues";
import { cIssuesUi } from "./IssuesUi";
import { cIssueDlg } from "./IssuesDlg";
import { cMessage } from "./Message";

let selectedIssue: number = 0;
let isSelection: boolean = false;

const issuesTableEvent = "issues.update-table";

const IssueTableWidget: FunctionComponent = () => {
  const iModelConnection = useActiveIModelConnection();
  const [issueList, setIssueList] = useState<Map<number, IssueData>>(new Map([]));

  useEffect(() => {
    getIssueList();

    cMessage.setupUiEvent(issuesTableEvent, getIssueList);
    return () => {
      cMessage.removeUiEvent(issuesTableEvent);
    };
  }, [])

  const dataProvider = (): SimpleTableDataProvider => {
    const columns: ColumnDescription[] = [];
    const columnProps: ColumnDescription = {
      key: "",
      label: "",
      resizable: true,
      showFieldFilters: true,
      showDistinctValueFilters: true,
      filterRenderer: FilterRenderer.MultiValue,
      filterable: true,
      sortable: true,
      editable: false,
      filterCaseSensitive: false,
      sortIgnoreCase: true,
    };

    columns.push({ ...columnProps, key: "id", label: "Id", width: 100 });
    columns.push({ ...columnProps, key: "type", label: "Type", width: 100 });
    columns.push({ ...columnProps, key: "title", label: "Title", width: 350 });
    columns.push({ ...columnProps, key: "status", label: "Status", width: 100 });
    columns.push({ ...columnProps, key: "priority", label: "Priority", width: 100});
    columns.push({ ...columnProps, key: "assigned", label: "Assigned To", width: 150});

    const dataProvider: SimpleTableDataProvider = new SimpleTableDataProvider(columns);

    for (const [id, issue] of issueList) {
      const rowItemKey = `${issue.id}`;
      const rowItem: RowItem = { key: rowItemKey, cells: [] };

      rowItem.cells.push(cellItem("id", "Id", issue.id.toString()));
      rowItem.cells.push(cellItem("type", "Type", issue.type));
      rowItem.cells.push(cellItem("title", "Title", issue.title));
      rowItem.cells.push(cellItem("status", "Status", issue.status));
      rowItem.cells.push(cellItem("priority", "Priority", issue.priority));
      rowItem.cells.push(cellItem("assigned", "Assigned To", issue.assignedTo));

      dataProvider.addRow(rowItem);
    }

    return dataProvider;
  }

  function cellItem(name: string, label: string, value: string): CellItem {
    const propValue: PropertyValue = {
      valueFormat: PropertyValueFormat.Primitive,
      value: value,
    };
    const description: PropertyDescription = {
      displayLabel: label,
      name: name,
      typename: "string",
    };
    const cell: CellItem = {
      key: name,
      record: new PropertyRecord(propValue, description),
    };

    return cell;
  }

  function getIssues(): Map<number, IssueData> {
    const list = cIssue.getIssuesList();
    const sortList = new Map([...list.entries()].sort());
    return sortList;
  }

  const _onRowsSelected = async (
    rowIterator: AsyncIterableIterator<RowItem>
  ): Promise<boolean> => {
    const row = await rowIterator.next();
    const id = row.value.key;
    selectedIssue = id;
    isSelection = true;

    if (cIssueDlg.isDialogOpen()) {
      const issue = cIssue.getIssueById(Number(selectedIssue));
      if (issue) {
        cIssueDlg.updateIssue(issue);
      }
    }

    return true;
  };

  const getIssueList = async () => {
    setIssueList(getIssues());
  };

  const _getDataProvider = useCallback(dataProvider, [issueList]);

  return (
    <>
      <div style={{ height: "100%" }}>
        <Table
          dataProvider={_getDataProvider()}
          onRowsSelected={_onRowsSelected}
        />
      </div>
    </>
  );
};

export class cIssuesTable {
  public static isTableOpen() {
    if (ModelessDialogManager.getDialogInfo("issuesTable")) {
      return true;
    } else {
      return false;
    }
  }

  public static updateTable() {
    cMessage.raiseUiEvent(issuesTableEvent);
  }

  public static openTable() {
    if (this.isTableOpen()) return;

    ModelessDialogManager.openDialog(
      <Dialog
        id={"issuesTable"}
        opened={true}
        modal={false}
        resizable={true}
        onClose={cIssuesTable.closeDialog}
        onEscape={cIssuesTable.closeDialog}
        movable={true}
        title={"Events"}
        width={930}
        height={300}
        footer={cIssuesTable.getFooter() as string}
      >
        <IssueTableWidget />
      </Dialog>,
      "issuesTable"
    );
  }

  //        alignment={DialogAlignment.Center}

  public static closeDialog = () => {
    if (cIssueDlg.isDialogOpen()) cIssueDlg.closeDialog();
    ModelessDialogManager.closeDialog("issuesTable");
  };

  private static onOpenIssue = () => {
    if (isSelection) {
      const issueId = Number(selectedIssue);
      const issue = cIssue.getIssueById(issueId);
      if (issue) {
        cIssueDlg.updateIssue(issue);
      }
    }
  };

  private static onCloseTable = () => {
    cIssuesTable.closeDialog();
  };

  private static onGoTo = () => {
    if (isSelection) {
      const issueId = Number(selectedIssue);
      const issue = cIssue.getIssueById(issueId);
      if (issue) {
        cIssuesUi.goToIssue(issue);
      }
    } else {
      cMessage.showStatusBarMessage("Select an Event", "icon-cursor-click");
    }
  };

  public static getFooter(): React.ReactNode {
    return (
      <div className="issues-table-footer-container">
        <div className="issues-table-footer">
          <Button styleType="cta" onClick={this.onOpenIssue}>
            <span className="icon icon-more-vertical-circular"> </span>
            {"Open"}
          </Button>

          <Button styleType="cta" onClick={this.onGoTo}>
            <span className="icon icon-zoom-in-2"> </span>
            {"Go To"}
          </Button>

          <Button styleType="cta" onClick={this.onCloseTable}>
            <span className="icon icon-close-2"> </span>
            {"Close"}
          </Button>
        </div>
      </div>
    );
  }
}
